import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import localDB from './localDB';
import { buildTaskReminderPlans } from '../utils/taskSchedule';

const CHANNEL_ID = 'task-reminders';
const IDS_SETTING = 'localNotificationIds';

function taskKey(task) {
  return String(task?.server_id || task?.temp_id || task?.id || '');
}

function notificationId(key, slot) {
  const text = `${key}:${slot}`;
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (Math.abs(hash) % 2000000000) + 1;
}

function openTask(taskId) {
  if (!taskId) return;
  const url = `/tasks?task_id=${encodeURIComponent(taskId)}`;
  window.history.pushState({}, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export class NotificationService {
  constructor() {
    this.initialized = false;
    this.permissionGranted = false;
    this.listener = null;
    this.channelCreated = false;
  }

  isNative() {
    return Capacitor.isNativePlatform();
  }

  async init() {
    if (!this.isNative()) return false;

    try {
      if (!this.channelCreated) {
        await LocalNotifications.createChannel({
          id: CHANNEL_ID,
          name: 'Напоминания о задачах',
          description: 'Сроки и просроченные задачи SAVEL’EV CRM',
          importance: 4,
          visibility: 1,
          vibration: true,
        });
        this.channelCreated = true;
      }

      let permissions = await LocalNotifications.checkPermissions();
      if (permissions.display !== 'granted') {
        permissions = await LocalNotifications.requestPermissions();
      }
      this.permissionGranted = permissions.display === 'granted';

      if (!this.listener) {
        this.listener = await LocalNotifications.addListener(
          'localNotificationActionPerformed',
          ({ notification }) => openTask(notification?.extra?.taskId)
        );
      }
      this.initialized = true;
    } catch (error) {
      console.warn('Local notifications unavailable:', error);
      this.permissionGranted = false;
    }

    return this.permissionGranted;
  }

  async refreshTasks(tasks = null) {
    if (!this.isNative()) return;
    const granted = await this.init();
    if (!granted) return;

    const allTasks = Array.isArray(tasks) ? tasks : await localDB.getTasks();
    const previousIds = await localDB.getSyncSetting(IDS_SETTING, []);

    const now = Date.now();
    const horizon = now + (90 * 24 * 60 * 60 * 1000);
    const notifications = [];

    const activeTasks = allTasks
      .filter((task) => task && task.status !== 'completed' && task.due_date)
      .slice(0, 150);

    for (const task of activeTasks) {
      const key = taskKey(task);
      if (!key) continue;

      const baseExtra = { taskId: key };
      const plans = buildTaskReminderPlans(task);

      for (const plan of plans) {
        const planAt = plan.at.getTime();
        if (planAt <= now + 3000 || planAt > horizon) continue;
        notifications.push({
          id: notificationId(key, plan.slot),
          title: plan.title,
          body: task.title || 'Откройте SAVEL’EV CRM',
          channelId: CHANNEL_ID,
          schedule: { at: plan.at, allowWhileIdle: true },
          extra: baseExtra,
        });
      }
    }

    // Не сносим действующие напоминания до успешной постановки нового набора.
    // Иначе один временный сбой Android оставляет пользователя вообще без них.
    if (notifications.length > 0) {
      await LocalNotifications.schedule({ notifications });
    }
    const nextIds = notifications.map(({ id }) => id);
    const nextIdSet = new Set(nextIds);
    const staleIds = Array.isArray(previousIds)
      ? previousIds.filter((id) => !nextIdSet.has(id))
      : [];
    if (staleIds.length > 0) {
      await LocalNotifications.cancel({
        notifications: staleIds.map((id) => ({ id })),
      }).catch((error) => {
        console.warn('Stale task notifications were not cancelled:', error);
      });
    }
    await localDB.setSyncSetting(IDS_SETTING, nextIds);
    return { scheduled: nextIds.length, cancelled: staleIds.length };
  }

  async refreshTask() {
    await this.refreshTasks();
  }
}

const notificationService = new NotificationService();

export default notificationService;
